import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FlexBox } from '@ardoq/layout';
import { ImageContentType } from '@ardoq/api-types';

const SvgContainer = styled(FlexBox)`
  min-height: 100px;
  min-width: 100px;
`;

interface ContentComponentProps {
  src: string;
  centerView: VoidFunction;
}

const SvgContent = React.memo(({ src, centerView }: ContentComponentProps) => {
  useEffect(() => {
    centerView();
  }, [centerView]);

  return (
    <SvgContainer
      data-test-id="imagePreviewContent"
      id="imagePreviewContent"
      // SVG source comes from a server-controlled attachment endpoint; never from
      // user-supplied translation strings. If the source ever broadens, sanitise first.
      // oxlint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: src }}
      justify="center"
      align="center"
    />
  );
});

const ImageContent = React.memo(
  ({ src, centerView }: ContentComponentProps) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
      if (isLoaded && imgRef.current) {
        centerView(); // Center the image after it's fully loaded
      }
    }, [isLoaded, centerView]);

    return (
      <img
        data-test-id="imagePreviewContent"
        ref={imgRef}
        id="imagePreviewContent"
        src={src}
        onLoad={() => {
          setIsLoaded(true); // Image is loaded, trigger state change
        }}
      />
    );
  }
);

type ContentDisplayProps = {
  contentType: ImageContentType;
} & ContentComponentProps;

export const ContentDisplay = (props: ContentDisplayProps) => {
  if (props.contentType === ImageContentType.SVG) {
    return <SvgContent {...props} />;
  }
  return <ImageContent {...props} />;
};
